/**
 * Tour Helper Utilities
 *
 * Utility functions for tour filtering, tab availability checking,
 * and step condition evaluation.
 *
 * @author Andres Nava
 * @version 1.0.0
 */

import type { TourStep, OnboardingFlow } from "../types/onboarding";
import type { User } from "../../shared/types/user";
import {
  getPrimaryRole as resolvePrimaryRole,
  getUserRoles as getRoleNameList,
} from "./roleHelpers";
import {
  countAvailableDashboardRoles as countDashboardRolesFromStrings,
  hasMultipleDashboardRoles as hasMultipleDashboardRolesFromStrings,
  isRoleSwitcherTourTargetVisible,
  shouldSkipRoleSwitcherTourStep,
} from "../../shared/onboarding/roleSwitcherTourStep";
import { logger } from "./logger";

export {
  isRoleSwitcherTourTargetVisible,
  shouldSkipRoleSwitcherTourStep,
} from "../../shared/onboarding/roleSwitcherTourStep";
export { DASHBOARD_ROLE_MODULE_KEYS } from "../../shared/onboarding/roleSwitcherTourStep";

/**
 * Check if a DOM element exists for a tour step target
 */
export function elementExists(selector: string): boolean {
  if (!selector) return false;

  try {
    // Handle data-tour selectors
    const dataTourMatch = selector.match(/data-tour(?:-stable)?=["']([^"']+)["']/);
    if (dataTourMatch) {
      const tourId = dataTourMatch[1];
      return (
        document.querySelector(`[data-tour-stable="${tourId}"]`) !== null ||
        document.querySelector(`[data-tour="${tourId}"]`) !== null
      );
    }

    // Handle CSS selectors
    return document.querySelector(selector) !== null;
  } catch (error) {
    logger.warn("Error checking element existence", {
      error: error instanceof Error ? error.message : String(error),
      selector,
    });
    return false;
  }
}

/**
 * Check if multiple elements exist (for conditionally rendered steps)
 */
export function elementsExist(selectors: string[]): boolean {
  if (!selectors || selectors.length === 0) return false;
  return selectors.some((selector) => elementExists(selector));
}

/**
 * Extract user roles as string array (handles both UserRoleInfo[] and string[])
 */
export function getUserRolesAsStrings(user: User | null | undefined): string[] {
  if (!user) return [];

  const userRoles: string[] = [];

  // Add primary role (deprecated but still used)
  if (user.role !== undefined && user.role !== null && user.role !== '') {
    userRoles.push(typeof user.role === 'string' ? user.role : String(user.role));
  }

  // Add additional roles
  if (user.roles && Array.isArray(user.roles)) {
    user.roles.forEach((role) => {
      if (typeof role === "string") {
        userRoles.push(role);
      } else if (role && typeof role === "object" && "name" in role) {
        userRoles.push(role.name);
      }
    });
  }

  return userRoles;
}

export function countAvailableDashboardRoles(
  user: User | null | undefined,
): number {
  if (!user) return 0;
  return countDashboardRolesFromStrings(getUserRolesAsStrings(user));
}

export function hasMultipleDashboardRoles(
  user: User | null | undefined,
): boolean {
  return hasMultipleDashboardRolesFromStrings(getUserRolesAsStrings(user));
}

/**
 * Check if user has required role(s) for a tour
 */
export function hasRequiredRole(
  user: User | null | undefined,
  requiredRole?: string | string[],
): boolean {
  if (
    requiredRole === undefined ||
    requiredRole === null ||
    (Array.isArray(requiredRole) && requiredRole.length === 0)
  ) {
    return true;
  }
  if (!user) return false;

  const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
  const userRoles = getUserRolesAsStrings(user);

  // Development-only test account override for local QA.
  if (import.meta.env.DEV && user.email) {
    const envAllowList = import.meta.env.VITE_ONBOARDING_TEST_ACCOUNT_EMAILS as
      | string
      | undefined;
    const allowedEmails = (envAllowList || "")
      .split(",")
      .map((email) => email.trim().toLowerCase())
      .filter(Boolean);
    if (allowedEmails.includes(user.email.toLowerCase())) {
      return true;
    }
  }

  // Check if user has any of the required roles
  return roles.some((role) => userRoles.includes(role));
}

/**
 * Filter tours based on user roles
 */
export function filterToursByRole(
  tours: OnboardingFlow[],
  user: User | null | undefined,
): OnboardingFlow[] {
  if (!user) return [];

  return tours.filter((tour) => {
    if (!tour.active) return false;
    if (!tour.requiredRole) return true; // No role requirement
    return hasRequiredRole(user, tour.requiredRole);
  });
}

/**
 * Validate a tour flow definition before running it.
 * Returns a list of validation errors (empty when valid).
 */
export function validateTourFlow(flow: OnboardingFlow): string[] {
  const errors: string[] = [];

  if (!flow.id || flow.id.trim().length === 0) {
    errors.push("Flow id is required.");
  }

  if (!flow.name || flow.name.trim().length === 0) {
    errors.push("Flow name is required.");
  }

  if (!Array.isArray(flow.steps)) {
    errors.push("Flow steps must be an array.");
    return errors;
  }

  if (flow.steps.length === 0) {
    errors.push("Flow has no steps defined.");
    return errors;
  }

  const stepNames = new Set<string>();
  flow.steps.forEach((step, index) => {
    if (!step.stepName || step.stepName.trim().length === 0) {
      errors.push(`Step ${index + 1} is missing stepName.`);
    } else if (stepNames.has(step.stepName)) {
      errors.push(`Duplicate stepName detected: ${step.stepName}.`);
    } else {
      stepNames.add(step.stepName);
    }

    if (!step.title || step.title.toString().trim().length === 0) {
      errors.push(`Step ${index + 1} is missing title.`);
    }

    if (!step.content) {
      errors.push(`Step ${index + 1} is missing content.`);
    }

    if (step.target && typeof step.target !== "string") {
      errors.push(`Step ${index + 1} target must be a selector string.`);
    }

    if (!step.target && !step.placement) {
      errors.push(
        `Step ${index + 1} should define placement when target is omitted.`,
      );
    }
  });

  return errors;
}

/**
 * Check if a step should be skipped based on skipIf condition
 */
export async function shouldSkipStep(step: TourStep): Promise<boolean> {
  if (!step.skipIf) return false;

  try {
    const result = step.skipIf();
    // Handle both sync and async skipIf functions
    if (result instanceof Promise) {
      return await result;
    }
    return Boolean(result);
  } catch (error) {
    logger.warn("Error evaluating skipIf condition", {
      error: error instanceof Error ? error.message : String(error),
      stepName: step.stepName,
    });
    return false;
  }
}

/**
 * Filter steps based on element existence and skip conditions
 */
export async function filterValidSteps(steps: TourStep[]): Promise<TourStep[]> {
  const validSteps: TourStep[] = [];

  for (const step of steps) {
    if (!step.target) {
      // No target means center placement - always valid
      const shouldSkip = await shouldSkipStep(step);
      if (!shouldSkip) {
        validSteps.push(step);
      }
      continue;
    }

    if (typeof step.target !== "string") {
      const el = step.target;
      const mounted = el instanceof Element && document.body.contains(el);
      if (!mounted) {
        continue;
      }
      const shouldSkip = await shouldSkipStep(step);
      if (!shouldSkip) {
        validSteps.push(step);
      }
      continue;
    }

    const target = step.target;

    // Check if this is a dropdown item (they're in portals and only exist when menu is open)
    // Dropdown items have patterns like: nav-browse-*, nav-about-us-*, nav-user-* (but not the buttons themselves)
    const isBrowseDropdownItem =
      target.includes("nav-browse-") &&
      !target.includes("nav-browse-button");
    const isAboutDropdownItem =
      target.includes("nav-about-us-") &&
      !target.includes("nav-about-us-button");
    const isUserDropdownItem =
      target.includes("nav-user-") &&
      target !== '[data-tour="nav-user-menu"]' &&
      target !== '[data-tour="nav-theme-toggle"]';

    const isAttendeePanelTarget =
      target.includes("attendee-tab-se-applications") ||
      target.includes("attendee-tab-se-assignments") ||
      target.includes("attendee-training-geo-alerts");

    const isModulePanelTarget =
      target.includes("payout-status") ||
      target.includes("payout-info") ||
      target.includes("stripe-setup-button") ||
      step.metadata?.isPanelTarget === true;

    const isDropdownItem =
      isBrowseDropdownItem || isAboutDropdownItem || isUserDropdownItem;

    // For dropdown items, skip element existence check (they'll be validated when dropdown opens)
    if (isDropdownItem || isAttendeePanelTarget || isModulePanelTarget) {
      // Check skipIf condition (but don't check element existence)
      const shouldSkip = await shouldSkipStep(step);
      if (!shouldSkip) {
        validSteps.push(step);
      }
      continue;
    }

    // For non-dropdown items, check element existence first
    if (!elementExists(target)) {
      continue;
    }

    // Check skipIf condition
    const shouldSkip = await shouldSkipStep(step);
    if (shouldSkip) {
      continue;
    }

    validSteps.push(step);
  }

  return validSteps;
}

/**
 * Check if a tab is available for the current user role
 */
export function isTabAvailable(tabId: string, userRoles: string[]): boolean {
  // Role-specific tab mappings
  const roleTabMap: Record<string, string[]> = {
    coordinator: [
      "coordinator-tab-members",
      "coordinator-tab-events",
      "coordinator-tab-venues",
      "coordinator-tab-embed",
      "coordinator-tab-approvals",
      "coordinator-tab-payouts",
      "coordinator-tab-settings",
      "coordinator-tab-approval-settings",
      "coordinator-tab-certificates",
      "coordinator-tab-outside-training",
    ],
    training_manager: [
      "training-manager-tab-events",
      "training-manager-tab-instructors",
      "training-manager-tab-tasks",
      "training-manager-tab-notifications",
      "training-manager-tab-venues",
    ],
    attendee: [
      "attendee-tab-events",
      "attendee-tab-certificates",
      "attendee-tab-outside-training",
      "attendee-tab-badges",
      "attendee-tab-leaderboard",
    ],
  };

  // Check if tab exists for any of the user's roles
  for (const role of userRoles) {
    if (roleTabMap[role]?.includes(tabId)) {
      return true;
    }
  }

  return false;
}

/** Same priority as dashboard tab selection (admin before coordinator, etc.). */
export function getPrimaryRole(user: User | null | undefined): string | null {
  return resolvePrimaryRole(user);
}

/**
 * Get role-specific dashboard tour ID
 */
const DASHBOARD_TOUR_BY_ROLE: Record<string, string> = {
  coordinator: 'dashboard_coordinator',
  training_manager: 'dashboard_training_manager',
  supervisor: 'dashboard_supervisor',
  instructor: 'dashboard_instructor',
  attendee: 'dashboard_attendee',
  tacticore: 'dashboard_tacticore',
  k9_ops: 'dashboard_k9_ops',
};

export function getRoleSpecificTourId(
  user: User | null | undefined,
): string | undefined {
  if (!user) return undefined;
  const primary = getPrimaryRole(user);
  if (primary && DASHBOARD_TOUR_BY_ROLE[primary]) {
    return DASHBOARD_TOUR_BY_ROLE[primary];
  }
  const modulePriority = [
    "coordinator",
    "training_manager",
    "supervisor",
    "instructor",
    "attendee",
    "tacticore",
    "k9_ops",
  ] as const;
  const roleNames = getRoleNameList(user);
  const match = modulePriority.find(
    (key) => roleNames.includes(key) && DASHBOARD_TOUR_BY_ROLE[key],
  );
  return match ? DASHBOARD_TOUR_BY_ROLE[match] : undefined;
}

/**
 * Wait for an element to appear in the DOM (useful for dynamically loaded content)
 */
export async function waitForElement(
  selector: string,
  timeout: number = 5000,
  interval: number = 100,
): Promise<Element | null> {
  const startTime = Date.now();

  return new Promise((resolve) => {
    // Check immediately
    const element = document.querySelector(selector);
    if (element) {
      resolve(element);
      return;
    }

    // Poll for element
    const intervalId = setInterval(() => {
      const element = document.querySelector(selector);
      if (element) {
        clearInterval(intervalId);
        resolve(element);
        return;
      }

      if (Date.now() - startTime >= timeout) {
        clearInterval(intervalId);
        resolve(null);
      }
    }, interval);
  });
}

/**
 * Check if a tour step target is ready (exists and is visible)
 */
export async function isStepTargetReady(
  step: TourStep,
  waitTimeout: number = 2000,
): Promise<boolean> {
  if (!step.target) return true; // No target means center placement

  let element: Element | null = null;

  if (typeof step.target !== "string") {
    element = step.target instanceof Element ? step.target : null;
    if (!element || !document.body.contains(element)) {
      return false;
    }
  } else {
    if (!elementExists(step.target)) {
      element = await waitForElement(step.target, waitTimeout);
      if (!element) return false;
    } else {
      element = document.querySelector(step.target);
    }
  }

  try {
    if (!element) return false;

    const style = window.getComputedStyle(element);
    return (
      style.display !== "none" &&
      style.visibility !== "hidden" &&
      style.opacity !== "0"
    );
  } catch (error) {
    logger.warn("Error checking element visibility", {
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
}
